import { useTranslation } from 'react-i18next';
import { useLoaderData, useRouteLoaderData } from '@shared/lib/routing';
import { Preloader } from '@shared/ui/preloader';
import { PageBanner, PageCustomText, PageHelmet } from '@widgets/seoPage';
import { PageContainer } from '@shared/ui/content-containers';
import ErrorPage from 'frontend/Pages/ErrorPage/ErrorPage';
import { Breadcrumbs } from '@shared/ui/breadcrumbs';
import { PageTitle } from '@shared/ui/titles';
import { PublicationSingle } from '@entities/publication';
import { LinkButton } from '@shared/ui/button';
import { OneNewsPageLoader } from '../api';

export function OneNewsPage() {
  const { pageData, loading } =
    useLoaderData<Awaited<ReturnType<typeof OneNewsPageLoader>>>();
  const { country, city } = useRouteLoaderData('city-layout');
  const { t } = useTranslation();

  if (loading) {
    return <Preloader />;
  }

  const {
    seoPageData,
    oneNewsPageData: { news },
  } = pageData;

  if (news && news.length === 0) {
    return <ErrorPage action_link="/news" action_text={t('news.newsGetAll')} />;
  }

  const {
    publication_from,
    title,
    description,
    images,
    announcement_text,
    name,
    category_name,
  } = news[0];

  return (
    <>
      <PageBanner
        bannerData={seoPageData.banner}
        mainDomain={country.main_domain}
      />
      <PageHelmet
        cityName={city.city_name}
        mainDomain={country.main_domain}
        seo={seoPageData.seo}
        type="news"
        title={title}
        image={images?.[0]?.filename}
        ogTitle={title}
        ogDescription={announcement_text}
      />
      <PageContainer>
        <Breadcrumbs
          links={[
            {
              href: '/news',
              text: t('news.newsTitle'),
            },
            {
              text: title,
            },
          ]}
        />

        <PageTitle>{title}</PageTitle>
        <PublicationSingle
          publicationDate={publication_from}
          title={title}
          description={description}
          imageFilename={images?.[0]?.filename}
          announcementText={announcement_text}
          mainDomain={country.main_domain}
          socialElementsTitle={name}
          categoryName={category_name}
          schemaLink="http://schema.org/NewsArticle"
        />
        <div className="flex justify-center">
          <LinkButton href="/news">{t('news.newsGetAll')}</LinkButton>
        </div>
        <PageCustomText
          customText={seoPageData.seo.custom_text}
          mainDomain={country.main_domain}
        />
      </PageContainer>
    </>
  );
}
